/**
 * Mini TV Baseball execution: video sequence, game box, footer, overlays.
 * Same structure and styles as platform-tv BaseballExecutionScreen; relative sizing for mini viewport.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import Emitter from 'shared/events/emitter';
import EVENT_NAMES from 'shared/constants/events';
import OverlayContainer from 'features/video/components/overlays/OverlayContainer';
import EventBoxBase from './EventBoxBase';
import MiniTvUiOverlayContext from './MiniTvUiOverlayContext';
import { withBasePath } from 'shared/utils/runtimeBasePath';

const TRANSITION_DURATIONS = { 1: 5000, 2: 4800, 3: 4200, 4: 4000, 5: 3800, 6: 3500, 7: 3200, 8: 3000 };

const JOIN_LATE_LOADER_TIMEOUT_MS = 10000;
const TRANSITION_MAX_WAIT_MS = 15000;

import { useHlsOrSrc } from 'features/video/components/mini-tv/useHlsOrSrc';
import { useSegmentPreloader } from 'features/video/hooks/useSegmentPreloader';

function escapeHtml(s) {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

export default function MiniTvBaseballExecution({
  eventId,
  event,
  eventResults,
  videoUrls,
  avh,
  onVideoReady,
  onExecutionEnded,
  initialSegmentIndex,
  initialSegmentSeekSeconds,
  muted = true,
  baseballBarAnchorRef,
}) {
  const { t } = useTranslation();
  const uiOverlayRef = React.useContext(MiniTvUiOverlayContext);
  const [uiOverlayTarget, setUiOverlayTarget] = React.useState(null);
  React.useEffect(() => {
    if (uiOverlayRef?.current && !uiOverlayTarget) setUiOverlayTarget(uiOverlayRef.current);
  }, [uiOverlayRef?.current, uiOverlayTarget]);
  const numVideos = Math.max(videoUrls?.length ?? 0, 7);
  const startIndex =
    initialSegmentIndex != null && videoUrls?.length
      ? Math.min(Math.max(0, Number(initialSegmentIndex)), videoUrls.length - 1)
      : 0;
  const [currentIndex, setCurrentIndex] = React.useState(startIndex);
  const initialSeekAppliedRef = React.useRef(false);
  const isJoinLate = initialSegmentIndex > 0 || (initialSegmentSeekSeconds != null && Number(initialSegmentSeekSeconds) > 0);
  const [joinLateReady, setJoinLateReady] = React.useState(() => !isJoinLate);
  React.useEffect(() => {
    if (initialSegmentIndex == null || !videoUrls?.length) return;
    const idx = Math.min(Math.max(0, Number(initialSegmentIndex)), videoUrls.length - 1);
    setJoinLateReady(!(idx > 0 || (initialSegmentSeekSeconds != null && Number(initialSegmentSeekSeconds) > 0)));
    setCurrentIndex((prev) => {
      if (prev !== 0 || idx <= 0) return prev;
      return idx;
    });
    if (idx > 0) {
      const replaySeq = Array.isArray(eventResults?.video_is_replay_sequence) ? eventResults.video_is_replay_sequence : [];
      let ball = 0;
      for (let i = 1; i <= idx + 1; i++) {
        if (i <= replaySeq.length && replaySeq[i - 1] !== true) ball += 1;
      }
      setRevealedBall((prev) => (prev == null && ball > 0 ? ball : prev));
    }
  }, [initialSegmentIndex, videoUrls?.length, eventResults?.video_is_replay_sequence, initialSegmentSeekSeconds]);
  const ballForInitialSegment = (() => {
    if (startIndex <= 0) return 0;
    const replaySeq = Array.isArray(eventResults?.video_is_replay_sequence) ? eventResults.video_is_replay_sequence : [];
    let ball = 0;
    for (let i = 1; i <= startIndex + 1; i++) {
      if (i <= replaySeq.length && replaySeq[i - 1] !== true) ball += 1;
    }
    return Math.max(0, ball);
  })();
  const [revealedBall, setRevealedBall] = React.useState(ballForInitialSegment > 0 ? ballForInitialSegment : null);
  const [isVideoPlaying, setIsVideoPlaying] = React.useState(false);
  const mainRef = React.useRef(null);
  const nextRef = React.useRef(null);
  const executionRootRef = React.useRef(null);
  const isMain = currentIndex % 2 === 0;
  const transitionDuration = TRANSITION_DURATIONS[videoUrls?.length] ?? 3200;
  const preloader = useSegmentPreloader(videoUrls, avh, currentIndex);

  // Pause videos when tab is hidden.
  // Parent orchestrator handles clean-slate recovery on tab return.
  React.useEffect(() => {
    const onVisChange = () => {
      if (document.visibilityState !== 'visible') {
        console.warn('[MiniTvBaseball] Tab hidden — pausing execution videos');
        try { mainRef.current?.pause(); } catch { /* ignore */ }
        try { nextRef.current?.pause(); } catch { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', onVisChange);
    return () => document.removeEventListener('visibilitychange', onVisChange);
  }, []);

  React.useEffect(() => {
    return () => {
      const vids = [mainRef.current, nextRef.current].filter(Boolean);
      vids.forEach((v) => {
        try {
          v.pause?.();
          v.removeAttribute?.('src');
          v.load?.();
        } catch {
          // ignore
        }
      });
    };
  }, []);

  const getVisibleClass = (el) =>
    el?.id === 'c7-mini-tv-baseball-transition'
      ? 'c7-mini-tv-baseball-transition--visible'
      : 'c7-mini-tv-baseball-video-container--visible';

  const baseballTransition = React.useCallback((direction, el1, el2) => {
    if (!el1 || !el2) return;
    const outClass = direction === 'up' ? 'rotateCubeBottomOut' : 'rotateCubeTopOut';
    const inClass = direction === 'up' ? 'rotateCubeBottomIn' : 'rotateCubeTopIn';
    el2.classList.add(getVisibleClass(el2));
    el1.classList.add(outClass);
    el2.classList.add(inClass);
    window.setTimeout(() => el1.classList.remove(getVisibleClass(el1)), 500);
    window.setTimeout(() => {
      el1.classList.remove(outClass);
      el2.classList.remove(inClass);
    }, 600);
  }, []);

  const videoIdSequence = Array.isArray(eventResults?.video_id_sequence) ? eventResults.video_id_sequence : [];
  const outcomeSequence = Array.isArray(eventResults?.outcome_sequence) ? eventResults.outcome_sequence : [];
  const replaySequence = Array.isArray(eventResults?.video_is_replay_sequence) ? eventResults.video_is_replay_sequence : [];

  const isReplayVideoIndex = (idx1) => (replaySequence[idx1 - 1] === true);
  const ballNumberForVideoIndex = (idx1) => {
    let ball = 0;
    for (let i = 1; i <= idx1; i++) {
      if (i <= replaySequence.length && replaySequence[i - 1] !== true) ball += 1;
    }
    return Math.max(1, ball);
  };
  const outcomeByBall = {};
  let ball = 0;
  for (let i = 1; i <= outcomeSequence.length; i++) {
    if (!isReplayVideoIndex(i)) {
      ball += 1;
      const raw = outcomeSequence[i - 1];
      outcomeByBall[ball] = typeof raw === 'string' ? String(raw).trim() : '';
    }
  }
  const ballOutcomeChar = (ballIdx1) => {
    const raw = outcomeByBall[ballIdx1];
    if (!raw) return '';
    return String(raw).trim().charAt(0).toUpperCase() || '';
  };

  let strikeCount = 0;
  let ballCount = 0;
  const n = revealedBall != null ? revealedBall : 0;
  for (let b = 1; b <= n; b++) {
    const raw = outcomeByBall[b] ?? '';
    const u = raw.toUpperCase();
    const first = u.slice(0, 1);
    if (first === 'B' || u === 'BALL') ballCount += 1;
    else if (first === 'S' || u === 'STRIKE') strikeCount += 1;
    else if (first === 'F' || u === 'FOUL') { if (strikeCount < 2) strikeCount += 1; }
  }

  const currentBall = ballNumberForVideoIndex(currentIndex + 1);
  const currentVideoId = videoIdSequence[currentIndex] ?? null;
  const currentUrl = videoUrls?.[currentIndex];
  const nextUrl = videoUrls?.[currentIndex + 1];
  const batterName = eventResults?.batter_name ?? event?.batter_name ?? '';
  const team1Name = eventResults?.team1?.name ?? 'Team 1';
  const team2Name = eventResults?.team2?.name ?? 'Team 2';

  const currentVideoRef = isMain ? mainRef : nextRef;

  useHlsOrSrc(isMain ? mainRef : nextRef, currentUrl, avh);
  useHlsOrSrc(isMain ? nextRef : mainRef, nextUrl, avh);

  React.useEffect(() => {
    if (initialSegmentIndex != null && initialSegmentIndex >= 0) return;
    setCurrentIndex(0);
    setRevealedBall(null);
  }, [eventResults?.event_id, initialSegmentIndex]);

  React.useEffect(() => {
    const video = currentVideoRef?.current;
    if (!video || !currentUrl) return;
    const seekSec = initialSegmentSeekSeconds != null && Number(initialSegmentSeekSeconds) > 0 ? Number(initialSegmentSeekSeconds) : null;
    const onCanPlay = () => {
      console.log('[MiniTvBaseball] canplay fired', {
        readyState: video.readyState,
        paused: video.paused,
        currentTime: video.currentTime,
        segmentIndex: currentIndex,
      });
      if (seekSec != null && currentIndex === startIndex && !initialSeekAppliedRef.current) {
        initialSeekAppliedRef.current = true;
        const d = video.duration;
        const target = Number.isFinite(d) && d > 0 ? Math.min(seekSec, d) : seekSec;
        video.currentTime = target;
        const onSeeked = () => {
          video.removeEventListener('seeked', onSeeked);
          setJoinLateReady(true);
        };
        video.addEventListener('seeked', onSeeked);
      } else {
        setJoinLateReady(true);
      }
      onVideoReady?.();
      video.play().catch((err) => console.warn('[MiniTvBaseball] play() rejected:', err?.message || err));
    };
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 2) onCanPlay();
    return () => video.removeEventListener('canplay', onCanPlay);
  }, [currentIndex, currentUrl, isMain, onVideoReady, initialSegmentSeekSeconds, startIndex]);

  React.useEffect(() => {
    if (!isJoinLate) return;
    const t = window.setTimeout(() => setJoinLateReady(true), JOIN_LATE_LOADER_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [isJoinLate]);

  React.useEffect(() => {
    const video = currentVideoRef?.current;
    if (!video || !isJoinLate) return;
    const onError = () => setJoinLateReady(true);
    video.addEventListener('error', onError);
    return () => video.removeEventListener('error', onError);
  }, [currentIndex, isMain, isJoinLate]);

  React.useEffect(() => {
    const video = currentVideoRef?.current;
    if (!video) return;
    const onPlay = () => setIsVideoPlaying(true);
    const onPause = () => setIsVideoPlaying(false);
    const onEnded = () => setIsVideoPlaying(false);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    };
  }, [currentIndex, isMain]);

  React.useEffect(() => {
    if (currentIndex >= (videoUrls?.length ?? 0)) return;
    const video = currentVideoRef?.current;
    if (!video) return;
    const idx1 = currentIndex + 1;
    const ballNum = ballNumberForVideoIndex(idx1);
    const isReplay = isReplayVideoIndex(idx1);

    const maybeReveal = () => {
      if (revealedBall === currentBall) return;
      if (isReplay) return;
      const d = video.duration;
      const t = video.currentTime;
      if (!Number.isFinite(d) || d <= 0 || !Number.isFinite(t)) return;
      if (d - t <= 1.0) setRevealedBall(currentBall);
    };

    const onTimeUpdate = () => maybeReveal();
    const onEnded = () => {
      maybeReveal();
      const total = videoUrls?.length ?? 0;
      const isLast = currentIndex >= total - 1;
      const nextIndex = currentIndex + 1;
      const nextK = currentIndex + 2; // 1-based index of next video
      const nextIsReplay = isReplayVideoIndex(nextK);

      if (isLast) {
        const transitionEl = document.getElementById('c7-mini-tv-baseball-transition');
        const mainContainer = document.getElementById('c7-mini-tv-baseball-video-container-main');
        const nextContainerEl = document.getElementById('c7-mini-tv-baseball-video-container-next');
        const currentContainer = isMain ? mainContainer : nextContainerEl;

        const k = currentIndex + 1;
        const ballNumForTransition = ballNumberForVideoIndex(k);
        const outcomeText = outcomeSequence[currentIndex] != null ? String(outcomeSequence[currentIndex]).trim() : '';
        const ballNumberEl = document.getElementById('c7-mini-tv-baseball-transition-ball-number');
        const outcomeEl = document.getElementById('c7-mini-tv-baseball-transition-outcome');
        const batterNameEl = document.getElementById('c7-mini-tv-baseball-transition-batter-name');
        if (ballNumberEl) ballNumberEl.textContent = String(ballNumForTransition);
        if (outcomeEl) {
          outcomeEl.innerHTML = outcomeText
            ? outcomeText
                .split('')
                .map((c) => `<span class="c7-mini-tv-baseball-outcome-letter baseballOutcomeIn">${escapeHtml(c)}</span>`)
                .join('')
            : '';
        }
        if (batterNameEl) batterNameEl.textContent = batterName;

        executionRootRef.current?.setAttribute('data-transition-visible', 'true');
        baseballTransition('up', currentContainer, transitionEl);

        window.setTimeout(() => {
          executionRootRef.current?.removeAttribute('data-transition-visible');
          onExecutionEnded?.();
        }, transitionDuration);
        return;
      }
      if (nextIsReplay) {
        setCurrentIndex(nextIndex);
        return;
      }

      const transitionEl = document.getElementById('c7-mini-tv-baseball-transition');
      const mainContainer = document.getElementById('c7-mini-tv-baseball-video-container-main');
      const nextContainerEl = document.getElementById('c7-mini-tv-baseball-video-container-next');
      const currentContainer = isMain ? mainContainer : nextContainerEl;
      const nextContainer = isMain ? nextContainerEl : mainContainer;

      const ballNumberEl = document.getElementById('c7-mini-tv-baseball-transition-ball-number');
      const outcomeEl = document.getElementById('c7-mini-tv-baseball-transition-outcome');
      const batterNameEl = document.getElementById('c7-mini-tv-baseball-transition-batter-name');

      const k = currentIndex + 1; // 1-based just-finished
      const ballNumForTransition = ballNumberForVideoIndex(k);
      const outcomeText = outcomeSequence[currentIndex] != null ? String(outcomeSequence[currentIndex]).trim() : '';
      if (ballNumberEl) ballNumberEl.textContent = String(ballNumForTransition);
      if (outcomeEl) {
        outcomeEl.textContent = '';
        outcomeEl.innerHTML = outcomeText
          ? outcomeText
              .split('')
              .map((c) => `<span class="c7-mini-tv-baseball-outcome-letter">${escapeHtml(c)}</span>`)
              .join('')
          : '';
      }
      if (batterNameEl) batterNameEl.textContent = batterName;

      const runOutcomeAnimation = () => {
        if (!outcomeEl) return;
        const letters = outcomeEl.querySelectorAll('.c7-mini-tv-baseball-outcome-letter');
        letters.forEach((letter) => letter.classList.add('baseballOutcomeIn'));
      };
      runOutcomeAnimation();

      executionRootRef.current?.setAttribute('data-transition-visible', 'true');
      baseballTransition('up', currentContainer, transitionEl);

      const nextVideo = (isMain ? nextRef : mainRef)?.current;
      const dismissTransition = () => {
        if (nextVideo) nextVideo.play().catch((err) => console.warn('[MiniTvBaseball] play() rejected:', err?.message || err));
        baseballTransition('down', transitionEl, nextContainer);
        executionRootRef.current?.removeAttribute('data-transition-visible');
        window.setTimeout(() => {
          setCurrentIndex(nextIndex);
        }, 100);
      };

      let minElapsed = false;
      let videoReady = nextVideo ? nextVideo.readyState >= 2 : true;
      let dismissed = false;
      const tryDismiss = () => {
        if (dismissed || !minElapsed || !videoReady) return;
        dismissed = true;
        dismissTransition();
      };
      const minTimer = window.setTimeout(() => { minElapsed = true; tryDismiss(); }, transitionDuration);
      const maxTimer = window.setTimeout(() => { if (!dismissed) { dismissed = true; dismissTransition(); } }, TRANSITION_MAX_WAIT_MS);
      if (!videoReady && nextVideo) {
        const onCanPlay = () => { nextVideo.removeEventListener('canplay', onCanPlay); videoReady = true; tryDismiss(); };
        nextVideo.addEventListener('canplay', onCanPlay);
      } else {
        tryDismiss();
      }
    };

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('ended', onEnded);
    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('ended', onEnded);
    };
  }, [currentIndex, videoUrls, transitionDuration, currentBall, revealedBall, onExecutionEnded, baseballTransition, outcomeSequence, batterName, isMain]);

  React.useEffect(() => {
    const video = currentVideoRef?.current;
    if (!video || !currentVideoId) return;
    const emitTime = () => Emitter.emit(EVENT_NAMES.MEDIA.TIMEUPDATE_PROCEED + '-' + currentVideoId, { time: video.currentTime });
    video.addEventListener('timeupdate', emitTime);
    return () => video.removeEventListener('timeupdate', emitTime);
  }, [currentIndex, currentVideoId, isMain]);

  const [portalTarget, setPortalTarget] = React.useState(null);
  React.useEffect(() => {
    const el = baseballBarAnchorRef?.current;
    if (el) setPortalTarget(el);
  }, [baseballBarAnchorRef]);

  const barContent = (
    <div className="c7-mini-tv-baseball-bar-overlay-wrap" style={{ ['--c7-mini-tv-overlay-scale']: 1 }}>
      <div className="c7-mini-tv-baseball-footer c7-mini-tv-baseball-footer--overlay">
      <div className="c7-mini-tv-baseball-footer-logo">
        <img src={withBasePath('/images/baseball/pelo7a-logo.png')} alt="pelo7a" />
      </div>
      <div className="c7-mini-tv-baseball-score-bar">
        <div className="c7-mini-tv-baseball-score-balls">
          {Array.from({ length: 7 }, (_, bi) => {
            const n = bi + 1;
            const isPast = n < currentBall;
            const isCurrent = n === currentBall;
            const isCurrentRevealed = isCurrent && revealedBall === n;
            const isActive = isPast || isCurrentRevealed;
            const shouldFlash = isVideoPlaying && isCurrent && !isCurrentRevealed;
            return (
              <React.Fragment key={n}>
                <span className="c7-mini-tv-baseball-ball-num">{n}</span>
                <div className={`c7-mini-tv-baseball-score-ball ${isActive ? 'c7-mini-tv-baseball-score-ball--active' : ''} ${shouldFlash ? 'c7-mini-tv-baseball-score-ball--flashing' : ''}`}>
                  <span className="c7-mini-tv-baseball-score-ball-outcome">{isActive ? ballOutcomeChar(n) : ''}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
    </div>
  );

  if (!videoUrls?.length) return null;

  return (
    <>
      {portalTarget && ReactDOM.createPortal(barContent, portalTarget)}
      <div
        ref={executionRootRef}
        className="c7-mini-tv-execution c7-mini-tv-baseball-execution c7-mini-tv-baseball-execution--perspective"
      >
      <div
        id="c7-mini-tv-baseball-transition"
        className="c7-mini-tv-baseball-transition"
        aria-hidden="true"
      >
        <div className="c7-mini-tv-baseball-transition-inner">
          <div className="c7-mini-tv-baseball-transition-box">
            <img src={withBasePath('/images/baseball/pelo7a-logo.png')} alt="pelo7a" />
            <img src={withBasePath('/images/baseball/event-box-logo.png')} alt="Event logo" />
          </div>
          <span id="c7-mini-tv-baseball-transition-batter-name" className="c7-mini-tv-baseball-transition-batter-name">
            {batterName}
          </span>
          <span id="c7-mini-tv-baseball-transition-ball-number" className="c7-mini-tv-baseball-transition-ball-number" />
          <span id="c7-mini-tv-baseball-transition-outcome" className="c7-mini-tv-baseball-transition-outcome" />
        </div>
      </div>
      <div
        id="c7-mini-tv-baseball-video-container-main"
        className={`c7-mini-tv-baseball-video-container${isMain ? ' c7-mini-tv-baseball-video-container--visible' : ''}`}
      >
        <div className="c7-mini-tv-baseball-media-wrapper">
          <div className="c7-mini-tv-baseball-video-inner">
            <video ref={mainRef} id="c7-mini-tv-baseball-video-main" playsInline muted={muted} />
          </div>
          {isMain && currentVideoId && (
            <div className="c7-mini-tv-overlay-wrapper">
              <OverlayContainer video_id={currentVideoId} scale_factor={1} />
            </div>
          )}
        </div>
      </div>
      <div
        id="c7-mini-tv-baseball-video-container-next"
        className={`c7-mini-tv-baseball-video-container${!isMain ? ' c7-mini-tv-baseball-video-container--visible' : ''}`}
      >
        <div className="c7-mini-tv-baseball-media-wrapper">
          <div className="c7-mini-tv-baseball-video-inner">
            <video ref={nextRef} id="c7-mini-tv-baseball-video-next" playsInline muted={muted} />
          </div>
          {!isMain && currentVideoId && (
            <div className="c7-mini-tv-overlay-wrapper">
              <OverlayContainer video_id={currentVideoId} scale_factor={1} />
            </div>
          )}
        </div>
      </div>
      {!joinLateReady && isJoinLate && (
        <div className="c7-mini-tv-join-late-loader" aria-hidden>
          <div className="c7-mini-tv-join-late-loader__spinner" />
        </div>
      )}
      </div>
      {uiOverlayTarget && ReactDOM.createPortal(
        <>
          <div className="c7-mini-tv-baseball-game-box">
            <div className="c7-mini-tv-baseball-game-box-batter-row">
              <span className="c7-mini-tv-baseball-game-box-batter-name">{batterName}</span>
              <div className="c7-mini-tv-baseball-current-ball">
                <span className="c7-mini-tv-baseball-current-ball-num">{currentBall}</span>
              </div>
            </div>
            <div className="c7-mini-tv-baseball-game-teams">{team1Name} vs {team2Name}</div>
            <div className="c7-mini-tv-baseball-game-box-stats-row">
              <div className="c7-mini-tv-baseball-ball-box"><span>{t('BALL')} : {ballCount}</span></div>
              <div className="c7-mini-tv-baseball-strike-box"><span>{t('STRIKE')} : {strikeCount}</span></div>
              <div className="c7-mini-tv-baseball-bases">
                <img src={withBasePath('/images/baseball/bases.png')} alt="" />
              </div>
            </div>
          </div>
          <EventBoxBase eventId={eventId} />
        </>,
        uiOverlayTarget
      )}
    </>
  );
}
